"use client";

import { faComputerMouseScrollwheel } from "@fortawesome/pro-solid-svg-icons";
import { Anchor, cn } from "@tosize/ui";
import { FocusTrap } from "focus-trap-react";
import { FormattedMessage, IntlProvider } from "react-intl";
import { RemoveScroll } from "react-remove-scroll";
import CanvasVisual, { CanvasVisualExpandedView } from "./components/App/CanvasVisual";
import { Chat } from "./components/App/Chat";
import Menu from "./components/App/Menu/Index";
import Order from "./components/App/Order";
import { Canvas } from "./components/renderer/Canvas";
import type { ConfigContextValue } from "./context/ConfigContext";
import { ConfigContextProvider } from "./context/ContextProvider";
import { CabinetStoreProvider } from "./context/StoreProvider";
import { useCanvasStore } from "./hooks/useCanvasStore";

export type FurnitureConfiguratorProps = ConfigContextValue;

export default function Index(props: FurnitureConfiguratorProps) {
	const { fullScreen, altControls } = useCanvasStore();
	const { messages = {}, ...config } = props;

	return (
		<IntlProvider locale={props.lang} messages={messages} onError={() => null}>
			<CabinetStoreProvider
				orderData={config.orderData}
				api={config.data}
				material={config.material}
				thickness={config.thickness}
			>
				<ConfigContextProvider config={{ ...config }}>
					<RemoveScroll enabled={!!fullScreen || altControls} forwardProps>
						<FocusTrap active={false} paused={!!fullScreen}>
							{config.mode === "view" ? (
								<Canvas />
							) : config.mode === "expandedView" ? (
								<div className="relative h-full w-full">
									<Canvas />
									<CanvasVisualExpandedView />
								</div>
							) : fullScreen ? (
								<div className="fixed inset-0 z-dialog bg-white focus:outline-none">
									<div className="h-full w-full">
										<Canvas />
									</div>
								</div>
							) : (
								<div className="relative z-0 m-auto w-full max-w-screen-4xl overflow-hidden focus:outline-none xl:aspect-video xl:max-h-[var(--canvas-y,100%)] xl:min-h-[640px]">
									<div
										className="target aspect-video h-svh max-h-[var(--canvas-y,100%)] min-h-[496px] w-full xl:h-auto xl:min-h-[640px]"
										id="start-ordering"
									>
										<Canvas />
									</div>

									<div className="pointer-events-none child:pointer-events-auto z-dialog bg-shades-light xl:absolute xl:inset-0 xl:bg-transparent">
										<Menu />
										<Chat />
										<CanvasVisual />
										<Order />

										{!config.dialog && (
											<div className="absolute top-auto right-0 bottom-4 left-0 hidden flex-row items-end justify-center space-x-2 xl:flex">
												<Anchor
													href="#faq-items"
													starticon={faComputerMouseScrollwheel}
													iconClassName="animate-bounce"
												>
													<FormattedMessage defaultMessage="Meer informatie over de mogelijkheden" />
												</Anchor>
											</div>
										)}
									</div>
								</div>
							)}
						</FocusTrap>
					</RemoveScroll>
				</ConfigContextProvider>
			</CabinetStoreProvider>
		</IntlProvider>
	);
}

export function Loader({ mode }: Pick<ConfigContextValue, "mode">) {
	return (
		<div
			className={cn(
				"z-loader flex h-full w-full max-w-screen-4xl bg-white xl:aspect-video xl:max-h-[var(--canvas-y,95vh)]",
				{ "min-h-[496px]": mode !== "view" },
			)}
		/>
	);
}
